import { type FC, type ReactNode } from "react";

interface HoverNoteProps {
  /** What the bubble says. */
  note: ReactNode;
  /** What you hover. */
  children: ReactNode;
  className?: string;
}

/**
 * A hover bubble, for explaining a number too small to explain itself.
 *
 * Not the `title` attribute. That waits about a second before appearing,
 * can't be styled, and is easy to miss entirely — it read as nothing
 * happening. It also leaves the browser's text I-beam over the trigger, which
 * says "you can select this" rather than "hover me", so `cursor-default` is
 * part of the component rather than something each caller remembers.
 *
 * CSS-only via `group`, so it needs no state and appears the moment the
 * pointer arrives. The bubble is `pointer-events-none`, so it can't swallow a
 * click meant for what is underneath it.
 *
 * Not usable inside a scroll container. The bubble is absolutely positioned,
 * so an ancestor with `overflow-x-auto` clips it to its scroll box and you
 * see a sliver of the sentence — which is what happened in the scored-runs
 * table. Where the trigger lives in one of those, put the explanation
 * somewhere always visible, such as the section header, instead.
 */
export const HoverNote: FC<HoverNoteProps> = ({ note, children, className }) => (
  <span className={`group relative cursor-default ${className ?? ""}`}>
    {children}
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-52 -translate-x-1/2 rounded-md bg-limestone-950 px-2.5 py-1.5 text-left text-xs font-normal leading-snug text-white shadow-md group-hover:block"
    >
      {note}
    </span>
  </span>
);
